import { formatBpmJobResult } from "./utils/adminOps";
import { requestJson } from "./api/request";
export { apiFetch, requestJson } from "./api/request";

export const API_HOST =
  process.env.REACT_APP_API_HOST || "http://127.0.0.1:8000";
export const API_BASE = `${API_HOST}/collection`;
export const PROFILE_ENDPOINT = `${API_HOST}/auth/discogs/profile/`;

export const PAGE_SIZE = 15;
export const SEARCH_PAGE_SIZE = 15;

export const IMPORT_POLL_INTERVAL = 3000; // ms
export const ADMIN_POLL_INTERVAL = 3000; // ms
export const IDLE_TIMEOUT = 30000; // ms
export const IMPORT_PROGRESS_FLAG = "importInProgress";
export const ADMIN_PROGRESS_PREFIX = "adminOp:";

export function createSortedListFetcher(
  endpoint,
  propertyKey,
  fetcher = fetchJson,
) {
  const normalizedEndpoint = endpoint.startsWith("/")
    ? endpoint
    : `/${endpoint}`;
  return async function fetchSortedList() {
    const data = await fetcher(`${API_BASE}${normalizedEndpoint}`, {
      credentials: "include",
    });
    const list =
      propertyKey && Array.isArray(data?.[propertyKey])
        ? data[propertyKey]
        : Array.isArray(data)
          ? data
          : [];
    return [...list].sort((a, b) => a.localeCompare(b));
  };
}

export async function fetchJson(url, options = {}) {
  return (await requestJson(url, options)).data;
}

const searchCache = new Map();
const SEARCH_CACHE_LIMIT = 100;
const SEARCH_CACHE_TTL = 60000;
let cacheGeneration = 0;

function cacheSearch(key, entry) {
  searchCache.delete(key);
  searchCache.set(key, entry);
  if (searchCache.size > SEARCH_CACHE_LIMIT) {
    searchCache.delete(searchCache.keys().next().value);
  }
}

export function clearSearchCache() {
  cacheGeneration += 1;
  searchCache.clear();
}

export async function fetchSearch({
  query,
  page = 1,
  types = "",
  admin = false,
  signal,
  filters = {},
}) {
  if (signal?.aborted) throw new DOMException("Search cancelled", "AbortError");
  if (!query) return { results: [] };
  const generation = cacheGeneration;
  const key = JSON.stringify([admin, types, page, query, filters]);
  if (searchCache.has(key)) {
    const cached = searchCache.get(key);
    if (cached.expiresAt > Date.now()) {
      cacheSearch(key, cached);
      return cached.data;
    }
    // A pending request belongs to its caller's cancellation scope. Sharing it
    // with another controller would let one unmount cancel another search.
    if (cached.promise && cached.signal === signal && !signal?.aborted) {
      return cached.promise;
    }
  }
  const params = new URLSearchParams({
    q: query,
    page,
    page_size: SEARCH_PAGE_SIZE,
  });
  if (types) params.set("types", types);
  Object.entries(filters).forEach(([k, v]) => {
    if (Array.isArray(v)) v.forEach((val) => params.append(k, val));
    else if (v !== undefined && v !== null && v !== "") params.append(k, v);
  });
  const endpoint = admin ? `${API_BASE}/admin/search/` : `${API_BASE}/search/`;
  const entry = { signal };
  const promise = fetchJson(`${endpoint}?${params.toString()}`, {
    credentials: "include",
    signal,
  })
    .then((data) => {
      if (signal?.aborted)
        throw new DOMException("Search cancelled", "AbortError");
      if (generation === cacheGeneration && searchCache.get(key) === entry)
        cacheSearch(key, { data, expiresAt: Date.now() + SEARCH_CACHE_TTL });
      return data;
    })
    .catch((err) => {
      if (generation === cacheGeneration && searchCache.get(key) === entry)
        searchCache.delete(key);
      throw err;
    });
  entry.promise = promise;
  cacheSearch(key, entry);
  return promise;
}

export const fetchFormats = createSortedListFetcher("/formats/", "formats");

export const fetchGenres = createSortedListFetcher("/genres/", "genres");

export const fetchSubGenres = createSortedListFetcher(
  "/sub-genres/",
  "sub_genres",
);

export const fetchLabels = createSortedListFetcher("/labels/", "labels");

export const fetchBreakOptions = createSortedListFetcher("/breaks/", "breaks");

export const fetchTags = createSortedListFetcher("/tags/", "tags");

export function normalizeBreaks(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {}
    return value
      .split(",")
      .map((v) => v.replace(/[[\]'"]/g, "").trim())
      .filter(Boolean);
  }
  return [];
}

export function parseBpmValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const text = String(value).trim();
  if (!text) return null;

  const match = text.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;

  const numeric = Number(match[0]);
  return Number.isFinite(numeric) ? numeric : null;
}

export function getBpmColor(bpm) {
  const numeric = parseBpmValue(bpm);
  if (numeric == null) return "";
  if (numeric <= 79) {
    return "blue";
  }
  if (numeric <= 109) {
    return "green";
  }
  if (numeric <= 129) {
    return "orange";
  }
  return "red";
}

export function resolveBpmBadge(primary, fallback) {
  const hasDirect = primary !== null && primary !== undefined && primary !== "";
  const resolved = hasDirect ? primary : fallback;
  const hasResolved =
    resolved !== null && resolved !== undefined && resolved !== "";

  if (!hasResolved) {
    return {
      hasDirect,
      hasResolved: false,
      displayValue: "",
      numericValue: null,
      badgeClass: "",
      hasBadge: false,
      isDefault: false,
    };
  }

  const numericValue = parseBpmValue(resolved);
  const badgeClass = numericValue != null ? getBpmColor(numericValue) : "";
  const hasBadge = numericValue != null;
  const isDefault =
    !hasDirect &&
    fallback !== null &&
    fallback !== undefined &&
    fallback !== "";

  return {
    hasDirect,
    hasResolved,
    displayValue: String(resolved),
    numericValue,
    badgeClass,
    hasBadge,
    isDefault,
  };
}

export function normalizeTags(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
}

export function trackFlagText(track) {
  const flags = [];
  if (track.is_a_cappella) flags.push("A cappella");
  if (track.is_instrumental) flags.push("Instrumental");
  if (track.is_styrene) flags.push("Styrene");
  return flags.join(" / ");
}

export function resolveTrackArtists(track) {
  if (!track) return [];

  const normalize = (artist) => {
    if (!artist) return null;
    if (typeof artist === "string") {
      const name = artist.trim();
      return name ? { id: null, name } : null;
    }
    if (typeof artist === "object") {
      const name =
        artist.name ||
        artist.title ||
        artist.artist ||
        artist.artist_name ||
        "";
      const trimmed = typeof name === "string" ? name.trim() : "";
      if (!trimmed) return null;
      return {
        id: artist.id ?? null,
        name: trimmed,
      };
    }
    return null;
  };

  const fromArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map(normalize).filter((artist) => artist && artist.name);
  };

  const direct = fromArray(track.artists);
  if (direct.length > 0) return direct;

  const albumArtists = fromArray(track.album?.artists);
  if (albumArtists.length > 0) return albumArtists;

  const fallbackName =
    track.artist ||
    track.artist_name ||
    track.album?.artist ||
    track.album?.artist_name ||
    "";

  const name = typeof fallbackName === "string" ? fallbackName.trim() : "";
  if (!name) return [];

  const fallbackId =
    track.artist_id ??
    track.album?.artist_id ??
    (Array.isArray(track.album?.artists) &&
    track.album.artists[0] &&
    track.album.artists[0].id
      ? track.album.artists[0].id
      : null);

  return [{ id: fallbackId ?? null, name }];
}

export const ADMIN_OPS = [
  {
    key: "album-details",
    title: "Fetch Album Details",
    icon: "📄",
    endpoint: "albums/fetch-missing-details/",
    cancellable: true,
    description:
      "Retrieve missing album metadata (image, labels) from Discogs.",
    group: "Album Operations",
  },
  {
    key: "artist-details",
    title: "Fetch Artist Details",
    icon: "👤",
    endpoint: "artists/fetch-missing-details/",
    cancellable: true,
    description: "Fetch artists missing images and sync name variations.",
    formatResult: (data) => {
      const count = data.import_total ?? data.total ?? 0;
      const message = count
        ? `${count} artist${count === 1 ? "" : "s"} updated`
        : "No artists updated";
      return { message };
    },
    group: "Artist Operations",
  },
  {
    key: "album-details-single",
    title: "Fetch Details for Album",
    icon: "📄",
    endpoint: (id) => `albums/${id}/fetch-missing-details/`,
    cancellable: true,
    runningLabel: "Fetching",
    description:
      "Retrieve missing metadata including styrene flag for a single album.",
    group: "Album Operations",
    needsAlbum: true,
  },
  {
    key: "artist-details-single",
    title: "Fetch Details for Artist",
    icon: "👤",
    endpoint: (id) => `artists/${id}/fetch-missing-details/`,
    cancellable: true,
    runningLabel: "Fetching",
    description: "Retrieve missing images for a single artist from Discogs.",
    group: "Artist Operations",
    needsArtist: true,
  },
  {
    key: "update-featured-artists",
    title: "Update Featured Artists",
    icon: "🎤",
    endpoint: "albums/update-featured-artists/",
    cancellable: true,
    description: "Backfill featured artist relationships for existing albums.",
    group: "Album Operations",
  },
  {
    key: "update-album-sub-genres",
    title: "Update Album Sub-Genres",
    icon: "🎼",
    endpoint: "albums/update-sub-genres/",
    cancellable: true,
    description:
      "Refresh Discogs sub-genre data for every album in the collection.",
    group: "Album Operations",
  },
  {
    key: "album-fetch-bpm",
    title: "Fetch BPM for Album",
    icon: "🎵",
    endpoint: (id) => `albums/${id}/fetch-bpm/`,
    cancellable: true,
    group: "Album Operations",
    needsAlbum: true,
    description:
      "Retry tracks missing BPM on this album. Duration-only backfill is not selected. Existing lookup caches still apply.",
    runningLabel: "Fetching",
    formatResult: formatBpmJobResult,
  },
  {
    key: "tracks-fetch-bpm",
    title: "Retry Missing BPM or Duration",
    icon: "🎚️",
    endpoint: "tracks/fetch-bpm/",
    cancellable: true,
    group: "Track Operations",
    runningLabel: "Fetching",
    description:
      "Start or reuse a BPM job. User filters apply only to a new job; an existing running job may serve other users. Status follows the latest job, not the whole queue.",
    needsUser: true,
    formatResult: formatBpmJobResult,
  },
  {
    key: "track-fetch-bpm",
    title: "Fetch BPM for Track",
    icon: "🎚️",
    endpoint: (id) => `tracks/${id}/fetch-bpm/`,
    cancellable: true,
    group: "Track Operations",
    needsTrack: true,
    runningLabel: "Fetching",
    description:
      "Retry this track using existing lookup caches. Completion does not guarantee metadata was found.",
    formatResult: (data) =>
      `${data.items_updated ?? 0} tracks changed (BPM or duration)`,
  },
];
